import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { BrutalButton } from "../ui/BrutalButton";
import { PairChips } from "../ui/LangChip";
import { courseTitle } from "../../utils/language";
import { learnPath } from "../../utils/routes";
import { spring } from "../../utils/motion";
const quickPicks = [{
  target: "es",
  base: "en"
}, {
  target: "it",
  base: "en"
}, {
  target: "en",
  base: "ru"
}];
export function EmptyDashboard() {
  const {
    addCourse,
    user
  } = useLearner();
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const start = (target: string, base: string) => {
    addCourse(target, base);
    toast.success(`${courseTitle({
      target,
      base
    })} added`);
    navigate(learnPath({
      target,
      base
    }));
  };
  return <motion.section aria-labelledby="empty-title" initial={reduce ? false : {
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={spring.heavy} className="tex-dots rounded-panel border-3 border-line bg-brand p-6 text-white shadow-brutal-lg md:p-10" data-id="element-168" data-mp="251t1oyfp5sjk">
      <h1 id="empty-title" className="max-w-[18ch] text-[34px] font-bold leading-[1.05] tracking-[-0.02em] md:text-5xl" data-id="element-169" data-mp="2f2d4i5iv753w">
        Welcome, {user.name.split(" ")[0]}. Pick your first course.
      </h1>
      <p className="mt-4 max-w-[52ch] text-[17px] leading-relaxed text-white/90" data-id="element-170" data-mp="2szs1pwfd0h6f">
        Every course is a pair: the language you want, taught from one you already know. You can add more pairs any time.
      </p>
      <div className="mt-8 flex flex-wrap items-center gap-4" data-id="element-171" data-mp="32pbk70z5u90r">
        <BrutalButton to="/app/onboarding" size="lg" data-id="element-172" data-mp="32ro0l9p9sbbp">
          Set up in 2 minutes <ArrowRightIcon className="h-5 w-5" data-id="element-173" data-mp="36shm5wf8pe1f" />
        </BrutalButton>
        <BrutalButton to="/app/courses" size="lg" variant="surface" data-id="element-174" data-mp="3bpq90abc2690">
          Browse 60+ languages
        </BrutalButton>
      </div>
      <div className="mt-10" data-id="element-175" data-mp="1bojmslqfbq1r">
        <h2 className="text-sm font-semibold text-white/85" data-id="element-176" data-mp="1blnnou2uavbq">Popular right now</h2>
        <ul className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-177" data-mp="17o9hlbagh3ny">
          {quickPicks.map(p => <li key={`${p.target}-${p.base}`} data-id="element-178" data-mp="1lf4ia0aswegg">
              <motion.button type="button" onClick={() => start(p.target, p.base)} whileHover={reduce ? undefined : {
            y: -2
          }} whileTap={{
            scale: 0.97
          }} transition={spring.snappy} className="focus-ring flex w-full items-center gap-3 rounded-card border-3 border-line bg-surface px-4 py-3.5 text-left text-ink shadow-brutal-sm" data-id="element-179" data-mp="1gml697rrhrdr">
                <PairChips target={p.target} base={p.base} data-id="element-180" data-mp="1ze48vdhvj3f9" />
                <span className="pl-1 text-[15px] font-semibold" data-id="element-181" data-mp="9y4thennwhr6">{courseTitle(p)}</span>
              </motion.button>
            </li>)}
        </ul>
      </div>
    </motion.section>;
}